import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, KeyRound } from 'lucide-react';
import { useForm, useWatch } from 'react-hook-form';
import type { z } from 'zod';

import {
  AuthError,
  AuthHeading,
  authLinkClass,
  AuthPasswordField,
  AuthSubmit,
  changePasswordSchema,
  PasswordChecklist,
  requireAuth,
  useAuth,
} from '@/app/auth';

export const Route = createFileRoute('/login/alterar-senha')({
  beforeLoad: requireAuth,
  component: ChangePasswordScreen,
});

type Values = z.infer<ReturnType<typeof changePasswordSchema>>;

/** Troca de senha com a sessão ativa (link de "Meu perfil"). */
function ChangePasswordScreen() {
  const { t } = useLingui();
  const { session, pending, error } = useAuth();
  const navigate = useNavigate();
  const form = useForm<Values>({
    resolver: zodResolver(changePasswordSchema()),
    defaultValues: { oldPassword: '', password: '', passwordConfirmation: '' },
  });
  const password = useWatch({ control: form.control, name: 'password' });

  const onSubmit = form.handleSubmit(async ({ oldPassword, password: next }) => {
    const state = await session.changePassword(oldPassword, next);
    if (state.status === 'signedIn' && !state.error) await navigate({ to: '/' });
  });

  return (
    <form className="flex flex-col gap-7" noValidate onSubmit={(event) => void onSubmit(event)}>
      <AuthHeading icon={KeyRound} title={<Trans>Alterar senha</Trans>}>
        <Trans>Confirme a senha atual e escolha uma nova.</Trans>
      </AuthHeading>
      <AuthError message={error} />
      <div className="flex flex-col gap-4">
        <AuthPasswordField
          label={t`Senha atual`}
          autoComplete="current-password"
          registration={form.register('oldPassword')}
          error={form.formState.errors.oldPassword}
        />
        <AuthPasswordField label={t`Nova senha`} autoComplete="new-password" registration={form.register('password')} error={form.formState.errors.password} />
        <PasswordChecklist value={password} />
        <AuthPasswordField
          label={t`Confirmar nova senha`}
          autoComplete="new-password"
          registration={form.register('passwordConfirmation')}
          error={form.formState.errors.passwordConfirmation}
        />
      </div>
      <AuthSubmit pending={pending}>
        <Trans>Alterar senha</Trans>
      </AuthSubmit>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to="/" className={authLinkClass}>
          <ArrowLeft aria-hidden className="size-4" />
          <Trans>Voltar ao início</Trans>
        </Link>
        <Link to="/login/recuperar-senha" className={authLinkClass}>
          <Trans>Esqueceu sua senha?</Trans>
        </Link>
      </div>
    </form>
  );
}
